import { useState, useEffect } from 'react';
import PolicyViolationsService from '../../../../../../services/PolicyViolations';
import PerformanceFeedbackService from '../../../../../../services/PerformanceFeedback';
import { INITIAL_FORM_DATA } from './formConstants';
import { UserWithEmployee } from '../../UserSelectionModal';

interface FormData {
  name: string;
  dateOfIncident: string;
  position: string;
  department: string;
  supervisor: string;
  dateOfHire: string;
  policyViolationIds: string[];
  descriptionOfFeedback: string;
  immediateFeedback: string;
  expectationForImprovement: string;
  emailOfFeedback: string;
}

interface FormErrors {
  name?: string;
  dateOfIncident?: string;
  policyViolationIds?: string;
  descriptionOfFeedback?: string;
  expectationForImprovement?: string;
}

interface PolicyViolation {
  id: string;
  name: string;
}

interface PriorDiscussion {
  date_of_incident: string;
  type: string;
  policy_violated: string;
  action_taken?: string;
}

export const useFeedbackForm = (
  isOpen: boolean,
  token: string,
  currentUser?: UserWithEmployee,
  submittedForms?: Map<string, FormData>,
  editMode: boolean = false,
  feedbackId?: string,
) => {
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState<FormErrors>({});
  const [policyViolations, setPolicyViolations] = useState<PolicyViolation[]>([]);
  const [priorDiscussions, setPriorDiscussions] = useState<PriorDiscussion[]>([]);

  const loadPolicyViolations = async () => {
    try {
      const service = PolicyViolationsService(token);
      const response = await service.list();
      setPolicyViolations(response.data || []);
    } catch (error) {
      console.error('Error loading policy violations:', error);
      setPolicyViolations([]);
    }
  };

  const loadPriorDiscussions = async (userId: string) => {
    try {
      const service = PerformanceFeedbackService(token);
      const response = await service.getPriorDiscussions(userId);
      const raw = (response as any)?.data ?? response;
      const rows = Array.isArray(raw) ? raw : Array.isArray((raw as any)?.data) ? (raw as any).data : [];
      const policyLabel = (item: any) => {
        if (item.policy_violated) {
          return item.policy_violated;
        }
        if (item.policyViolation?.name) {
          return item.policyViolation.name;
        }
        if (typeof item.policyViolation === 'string') {
          return item.policyViolation;
        }
        if (Array.isArray(item.policyViolations) && item.policyViolations.length > 0) {
          return item.policyViolations
            .map((pv: any) => (typeof pv === 'string' ? pv : pv?.name || ''))
            .filter(Boolean)
            .join(', ');
        }
        return '';
      };
      const actionLabel = (item: any) => {
        const v = item.actionTaken ?? item.action_taken;
        if (typeof v === 'string' && v.trim()) {
          return v.trim();
        }
        return '';
      };
      setPriorDiscussions(
        rows.map((item: any) => ({
          date_of_incident: item.dateOfIncident ?? item.date ?? item.date_of_incident ?? '',
          type: item.type ?? 'feedback',
          policy_violated: policyLabel(item),
          action_taken: actionLabel(item),
        })),
      );
    } catch (error) {
      console.error('Error loading prior discussions:', error);
      setPriorDiscussions([]);
    }
  };

  const loadFeedbackData = async (id: string) => {
    try {
      const service = PerformanceFeedbackService(token);
      const response = await service.show(id);
      const feedback = response.data;

      if (feedback) {
        let policyViolationIds: string[] = [];

        if (feedback.policyViolationIds && Array.isArray(feedback.policyViolationIds)) {
          policyViolationIds = feedback.policyViolationIds;
        } else if (feedback.policyViolations && Array.isArray(feedback.policyViolations)) {
          policyViolationIds = feedback.policyViolations.map((pv: any) => pv.id || pv);
        } else if (feedback.policyViolationId) {
          policyViolationIds = [feedback.policyViolationId];
        } else if (feedback.policyViolation?.id) {
          policyViolationIds = [feedback.policyViolation.id];
        }

        setFormData({
          name: feedback.name || '',
          dateOfIncident: feedback.dateOfIncident ? new Date(feedback.dateOfIncident).toISOString().split('T')[0] : '',
          position: feedback.position || '',
          department: feedback.department || '',
          supervisor: feedback.supervisor || '',
          dateOfHire: feedback.dateOfHire ? new Date(feedback.dateOfHire).toISOString().split('T')[0] : '2024-01-01',
          policyViolationIds,
          descriptionOfFeedback: feedback.descriptionOfFeedback || '',
          immediateFeedback: feedback.immediateFeedback || '',
          expectationForImprovement: feedback.expectationForImprovement || '',
          emailOfFeedback: feedback.emailOfFeedback || '',
        });

        if (feedback.userId) {
          loadPriorDiscussions(feedback.userId);
        }
      }
    } catch (error) {
      console.error('Error loading feedback data:', error);
    }
  };

  useEffect(() => {
    if (isOpen && token) {
      loadPolicyViolations();
    }
  }, [isOpen, token]);

  useEffect(() => {
    if (isOpen && editMode && feedbackId && token) {
      loadFeedbackData(feedbackId);
    }
  }, [isOpen, editMode, feedbackId, token]);

  useEffect(() => {
    if (isOpen && !editMode && currentUser) {
      const savedForm = submittedForms?.get(currentUser.id);
      if (savedForm) {
        setFormData(savedForm);
      } else if (submittedForms && submittedForms.size > 0) {
        const templates = Array.from(submittedForms.values());
        const template = templates[templates.length - 1];
        setFormData({
          ...template,
          name: `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim(),
          position: currentUser.employee?.title?.name || '',
          department: currentUser.employee?.department?.name || '',
          supervisor: currentUser.employee?.supervisor?.name || '',
          dateOfHire: currentUser.employee?.startDate || '2024-01-01',
        });
      } else {
        setFormData({
          name: `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim(),
          dateOfIncident: '',
          position: currentUser.employee?.title?.name || '',
          department: currentUser.employee?.department?.name || '',
          supervisor: currentUser.employee?.supervisor?.name || '',
          dateOfHire: currentUser.employee?.startDate || '2024-01-01',
          policyViolationIds: [],
          descriptionOfFeedback: '',
          immediateFeedback: '',
          expectationForImprovement: '',
          emailOfFeedback: '',
        });
      }
      setErrors({});
      loadPriorDiscussions(currentUser.id);
    }
  }, [isOpen, editMode, currentUser?.id]);

  useEffect(() => {
    if (!isOpen) {
      setFormData(INITIAL_FORM_DATA);
      setErrors({});
      setPriorDiscussions([]);
    }
  }, [isOpen]);

  const handleInputChange = (field: keyof FormData, value: string | string[]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    const today = new Date().toISOString().split('T')[0];

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!formData.dateOfIncident) {
      newErrors.dateOfIncident = 'Date of Incident is required';
    } else if (formData.dateOfIncident > today) {
      newErrors.dateOfIncident = 'Date of Incident cannot be in the future';
    }
    if (formData.policyViolationIds.length === 0) {
      newErrors.policyViolationIds = 'At least one Policy Violation is required';
    }
    if (!formData.descriptionOfFeedback.trim()) {
      newErrors.descriptionOfFeedback = 'Description of Feedback is required';
    }
    if (!formData.expectationForImprovement.trim()) {
      newErrors.expectationForImprovement = 'Expectation for Improvement is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  return {
    formData,
    errors,
    policyViolations,
    priorDiscussions,
    handleInputChange,
    validateForm,
    setFormData,
  };
};
